import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCenter,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { CalendarIcon, MessageSquare, Paperclip, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useTasksCollab, type TaskItem } from '@/lib/collab/use-tasks-collab'
import { TaskPanel, type CreateTaskData } from '@/components/task-panel'
import { orpc } from '@/lib/orpc-client'
import { isAfter, isBefore, addDays, startOfDay } from 'date-fns'

interface ColumnDef {
  id: string
  /** Key under `tasks:board.column` — resolved via `t` at render so language switches apply live. */
  titleKey: 'todo' | 'inProgress' | 'review' | 'done'
  color: string
  headerColor: string
}

const COLUMNS: ColumnDef[] = [
  {
    id: 'todo',
    titleKey: 'todo',
    color: 'bg-muted/50',
    headerColor: 'bg-muted',
  },
  {
    id: 'in_progress',
    titleKey: 'inProgress',
    color: 'bg-blue-50 dark:bg-blue-950/20',
    headerColor: 'bg-blue-100 dark:bg-blue-900/40',
  },
  {
    id: 'review',
    titleKey: 'review',
    color: 'bg-amber-50 dark:bg-amber-950/20',
    headerColor: 'bg-amber-100 dark:bg-amber-900/40',
  },
  {
    id: 'done',
    titleKey: 'done',
    color: 'bg-green-50 dark:bg-green-950/20',
    headerColor: 'bg-green-100 dark:bg-green-900/40',
  },
]

const PRIORITY_COLORS: Record<number, string> = {
  1: 'border-l-red-500',
  2: 'border-l-red-500',
  3: 'border-l-red-500',
}

function getDueDateColor(dueDate: string | null): string | null {
  if (!dueDate) return null
  const due = new Date(dueDate)
  const today = startOfDay(new Date())
  if (isBefore(due, today)) return 'text-red-500'
  if (isBefore(due, addDays(today, 2))) return 'text-orange-500'
  return 'text-muted-foreground'
}

function formatDueDate(dueDate: string): string {
  const d = new Date(dueDate)
  return `${d.getDate()}/${d.getMonth() + 1}`
}

interface Member {
  userId: string
  name: string | null
  avatarUrl: string | null
}

// ─── Sortable Task Card ─────────────────────────────────────────

function SortableTaskCard({
  task,
  isRemoteDragging,
  onClick,
  memberMap,
}: {
  task: TaskItem
  isRemoteDragging: boolean
  onClick: () => void
  memberMap: Map<string, Member>
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cn(
        'bg-card rounded-lg border p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow',
        task.priority > 0 && 'border-l-3',
        task.priority > 0 && PRIORITY_COLORS[task.priority],
        isDragging && 'opacity-50',
        isRemoteDragging && 'ring-2 ring-blue-400/50 opacity-75'
      )}
    >
      <span className="font-medium text-sm leading-tight block">{task.title}</span>

      {/* Subtask progress bar */}
      {(task.subtaskTotal ?? 0) > 0 && (
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 bg-muted rounded-full h-1.5">
            <div
              className="bg-green-500 rounded-full h-full transition-all"
              style={{ width: `${((task.subtaskCompleted ?? 0) / task.subtaskTotal) * 100}%` }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {task.subtaskCompleted ?? 0}/{task.subtaskTotal}
          </span>
        </div>
      )}

      {/* Meta row */}
      {(task.tags.length > 0 ||
        task.assigneeIds.length > 0 ||
        task.dueDate ||
        (task.commentCount ?? 0) > 0 ||
        (task.attachmentCount ?? 0) > 0) && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {task.tags.slice(0, 3).map((tag) => (
            <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
              {tag}
            </Badge>
          ))}
          {task.tags.length > 3 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              +{task.tags.length - 3}
            </Badge>
          )}

          <div className="flex-1" />

          {task.dueDate && (
            <span className={cn('flex items-center gap-1 text-xs', getDueDateColor(task.dueDate))}>
              <CalendarIcon className="size-3" />
              {formatDueDate(task.dueDate)}
            </span>
          )}

          {(task.commentCount ?? 0) > 0 && (
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
              <MessageSquare className="size-3" />
              {task.commentCount}
            </span>
          )}

          {(task.attachmentCount ?? 0) > 0 && (
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
              <Paperclip className="size-3" />
              {task.attachmentCount}
            </span>
          )}

          {task.assigneeIds.length > 0 && (
            <div className="flex -space-x-1">
              {task.assigneeIds.slice(0, 3).map((uid) => {
                const m = memberMap.get(uid)
                return (
                  <Avatar key={uid} className="size-5 border-2 border-card">
                    {m?.avatarUrl && <AvatarImage src={m.avatarUrl} />}
                    <AvatarFallback className="text-[9px]">
                      {(m?.name ?? uid).substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )
              })}
              {task.assigneeIds.length > 3 && (
                <span className="flex items-center justify-center size-5 rounded-full bg-muted text-[9px] border-2 border-card">
                  +{task.assigneeIds.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Overlay Card (shown while dragging) ────────────────────────

function TaskCardOverlay({ task }: { task: TaskItem }) {
  return (
    <div
      className={cn(
        'bg-card rounded-lg border p-3 shadow-lg',
        task.priority > 0 && 'border-l-3',
        task.priority > 0 && PRIORITY_COLORS[task.priority]
      )}
    >
      <span className="font-medium text-sm leading-tight block">{task.title}</span>
    </div>
  )
}

// ─── Collision detection ────────────────────────────────────
// pointerWithin finds columns the cursor is inside, closestCenter
// picks the nearest sortable item. Prefer sortable hits so reorder
// within a column feels natural; fall back to the column droppable
// for cross-column moves (especially into empty columns).
const columnIds = new Set(COLUMNS.map((c) => c.id))

const multiContainerCollision: CollisionDetection = (args) => {
  // First try closestCenter — works great for task-to-task
  const centerHits = closestCenter(args)
  if (centerHits.length > 0) {
    // If the closest hit is a sortable item (task), use it
    const first = centerHits[0]
    if (!columnIds.has(String(first.id))) return centerHits
  }
  // Fall back to pointerWithin — detects column containers
  return pointerWithin(args)
}

// ─── Droppable Column ───────────────────────────────────────

function DroppableColumn({ columnId, children }: { columnId: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 p-2 space-y-2 overflow-y-auto min-h-[120px]',
        isOver && 'bg-accent/30 rounded-b-lg'
      )}
    >
      {children}
    </div>
  )
}

// ─── Main Board ─────────────────────────────────────────────────

export function KanbanBoard({
  projectId,
  initialTaskId,
}: {
  projectId: string
  initialTaskId?: string
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('tasks')

  const {
    columns,
    addTask,
    moveTask,
    reorderTask,
    updateTask,
    deleteTask,
    draggingUsers,
    setDragging,
    totalTaskCount,
    synced,
  } = useTasksCollab(projectId)

  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeCol, setActiveCol] = useState<string | null>(null)
  const dragOriginCol = useRef<string | null>(null)
  const [panelMode, setPanelMode] = useState<'closed' | 'create' | 'edit'>('closed')
  const [panelTask, setPanelTask] = useState<TaskItem | null>(null)
  const [panelExpanded, setPanelExpanded] = useState(false)
  const [members, setMembers] = useState<Member[]>([])
  const [customColumnNames, setCustomColumnNames] = useState<Map<string, string>>(new Map())
  const [editingColumn, setEditingColumn] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  // Load project members for avatars
  useEffect(() => {
    orpc.members
      .list({ projectId })
      .then((res) => setMembers(res.members))
      .catch(console.error)
  }, [projectId])

  // Load custom column names
  useEffect(() => {
    orpc.taskColumns
      .list({ projectId })
      .then((res) => {
        const map = new Map<string, string>()
        for (const item of res.items) map.set(item.columnId, item.displayName)
        setCustomColumnNames(map)
      })
      .catch(console.error)
  }, [projectId])

  const memberMap = useMemo(() => {
    const map = new Map<string, Member>()
    for (const m of members) map.set(m.userId, m)
    return map
  }, [members])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const tasksForColumn = (columnId: string): TaskItem[] => columns.get(columnId) ?? []

  const allTasks = useMemo(() => {
    const tasks: TaskItem[] = []
    for (const col of COLUMNS) {
      tasks.push(...tasksForColumn(col.id))
    }
    return tasks
  }, [columns])

  // Auto-open task panel when navigating with ?task=<id>
  useEffect(() => {
    if (!initialTaskId || !synced || allTasks.length === 0) return
    const task = allTasks.find((t) => t.id === initialTaskId)
    if (task) {
      setPanelTask(task)
      setPanelMode('edit')
    }
  }, [initialTaskId, synced, allTasks.length])

  const findColumnForTask = (taskId: string): string | null => {
    for (const col of COLUMNS) {
      const tasks = tasksForColumn(col.id)
      if (tasks.some((t) => t.id === taskId)) return col.id
    }
    return null
  }

  const activeTask = activeId ? (allTasks.find((t) => t.id === activeId) ?? null) : null

  const isRemoteDragging = (taskId: string) => draggingUsers.some((u) => u.taskId === taskId)

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id)
    const col = findColumnForTask(id)
    setActiveId(id)
    setActiveCol(col)
    dragOriginCol.current = col
    if (col) setDragging({ taskId: id, fromCol: col })
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeTaskId = String(active.id)
    const overId = String(over.id)

    const fromCol = findColumnForTask(activeTaskId)
    // Check if over is a column (droppable) or a task
    const toCol = COLUMNS.some((c) => c.id === overId) ? overId : findColumnForTask(overId)

    if (!fromCol || !toCol || fromCol === toCol) return

    // Cross-column move during drag
    const fromTasks = tasksForColumn(fromCol)
    const toTasks = tasksForColumn(toCol)
    const oldIndex = fromTasks.findIndex((t) => t.id === activeTaskId)
    if (oldIndex === -1) return

    let newIndex = toTasks.length
    if (!COLUMNS.some((c) => c.id === overId)) {
      // Over a task — insert at that index
      const overIndex = toTasks.findIndex((t) => t.id === overId)
      if (overIndex !== -1) newIndex = overIndex
    }

    moveTask(activeTaskId, fromCol, toCol, newIndex)
    setActiveCol(toCol)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    const originCol = dragOriginCol.current
    setActiveId(null)
    setActiveCol(null)
    dragOriginCol.current = null
    setDragging(null)

    if (!over) return

    const activeTaskId = String(active.id)
    const overId = String(over.id)

    // Dropped on a column droppable
    const isColumnDrop = COLUMNS.some((c) => c.id === overId)
    if (isColumnDrop) {
      const currentCol = findColumnForTask(activeTaskId)
      if (currentCol && currentCol !== overId) {
        const toTasks = tasksForColumn(overId)
        const newPos = toTasks.length
        moveTask(activeTaskId, currentCol, overId, newPos)
      }
    } else if (activeTaskId !== overId) {
      // Within-column reorder (dropped on another task)
      const col = findColumnForTask(activeTaskId)
      if (col) {
        const tasks = tasksForColumn(col)
        const oldIndex = tasks.findIndex((t) => t.id === activeTaskId)
        const newIndex = tasks.findIndex((t) => t.id === overId)
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          reorderTask(activeTaskId, col, newIndex)
        }
      }
    }

    // Always persist final position to DB after drop
    const finalCol = findColumnForTask(activeTaskId)
    if (finalCol) {
      const finalTasks = tasksForColumn(finalCol)
      const finalPos = finalTasks.findIndex((t) => t.id === activeTaskId)
      if (finalCol !== originCol || finalPos !== -1) {
        orpc.tasks
          .reorder({
            projectId,
            taskId: activeTaskId,
            columnName: finalCol,
            position: Math.max(finalPos, 0),
          })
          .catch(console.error)
      }
    }
    window.dispatchEvent(new CustomEvent('tasks-changed'))
  }

  const handleCreateTask = async (data: CreateTaskData) => {
    try {
      const dbTask = await orpc.tasks.create({
        projectId,
        title: data.title,
        descriptionJson: data.descriptionJson,
        columnName: 'todo',
        priority: data.priority,
        tags: data.tags,
        assigneeIds: data.assigneeIds,
        dueDate: data.dueDate,
      })
      addTask('todo', data.title, {
        id: dbTask.id,
        description: data.description,
        descriptionJson: data.descriptionJson,
        priority: data.priority,
        tags: data.tags,
        assigneeIds: data.assigneeIds,
        dueDate: data.dueDate,
      })
      // Switch panel to edit mode for the new task
      // Note: allTasks hasn't re-computed yet after addTask, so construct from dbTask
      setPanelTask({
        id: dbTask.id,
        title: data.title,
        description: data.description ?? '',
        descriptionJson: data.descriptionJson ?? null,
        status: 'open',
        position: 0,
        priority: data.priority ?? 0,
        tags: data.tags ?? [],
        assigneeId: null,
        assigneeIds: data.assigneeIds ?? [],
        dueDate: data.dueDate ?? null,
        commentCount: 0,
        attachmentCount: 0,
        subtaskTotal: 0,
        subtaskCompleted: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      setPanelMode('edit')
      window.dispatchEvent(new CustomEvent('tasks-changed'))
    } catch (err) {
      console.error('Failed to create task', err)
    }
  }

  const handleUpdateTask = (
    taskId: string,
    fields: Partial<
      Pick<
        TaskItem,
        | 'title'
        | 'description'
        | 'descriptionJson'
        | 'priority'
        | 'tags'
        | 'assigneeId'
        | 'assigneeIds'
        | 'dueDate'
        | 'commentCount'
        | 'attachmentCount'
        | 'subtaskTotal'
        | 'subtaskCompleted'
      >
    >
  ) => {
    updateTask(taskId, fields)
    // Only persist API-relevant fields (not subtask/attachment counts which are derived)
    const { subtaskTotal, subtaskCompleted, attachmentCount, ...apiFields } = fields
    if (Object.keys(apiFields).length > 0) {
      orpc.tasks.update({ projectId, taskId, ...apiFields }).catch(console.error)
    }
    if (fields.assigneeIds !== undefined) {
      window.dispatchEvent(new CustomEvent('tasks-changed'))
    }
  }

  const handleDeleteTask = (taskId: string) => {
    deleteTask(taskId)
    orpc.tasks.delete({ projectId, taskId }).catch(console.error)
    window.dispatchEvent(new CustomEvent('tasks-changed'))
  }

  const handleOpenDetail = (task: TaskItem) => {
    setPanelTask(task)
    setPanelMode('edit')
    setPanelExpanded(false)
    navigate({
      to: '/$projectId/tasks',
      params: { projectId },
      search: { task: task.id },
      replace: true,
    })
  }

  const handlePanelClose = () => {
    setPanelMode('closed')
    setPanelTask(null)
    setPanelExpanded(false)
    navigate({
      to: '/$projectId/tasks',
      params: { projectId },
      search: {},
      replace: true,
    })
  }

  const handleMoveTask = (taskId: string, toColumn: string) => {
    const fromColumn = findColumnForTask(taskId)
    if (!fromColumn || fromColumn === toColumn) return
    moveTask(taskId, fromColumn, toColumn, 0)
    orpc.tasks
      .reorder({ projectId, taskId, columnName: toColumn, position: 0 })
      .catch(console.error)
    window.dispatchEvent(new CustomEvent('tasks-changed'))
  }

  // Keep panelTask in sync with live collab data
  const livePanelTask = panelTask
    ? (allTasks.find((t) => t.id === panelTask.id) ?? panelTask)
    : null

  const getColumnDisplayName = (columnId: string) => {
    const custom = customColumnNames.get(columnId)
    if (custom) return custom
    const def = COLUMNS.find((c) => c.id === columnId)
    return def ? t(`board.column.${def.titleKey}`) : columnId
  }

  const saveColumnName = async (columnId: string) => {
    const name = editingName.trim()
    setEditingColumn(null)
    if (!name) return
    const oldName = getColumnDisplayName(columnId)
    if (name === oldName) return
    setCustomColumnNames((prev) => new Map(prev).set(columnId, name))
    try {
      await orpc.taskColumns.update({ projectId, columnId, displayName: name })
    } catch {
      // Revert
      setCustomColumnNames((prev) => {
        const m = new Map(prev)
        m.delete(columnId)
        return m
      })
    }
  }

  // Column defs for the panel board selector
  const columnDefs = COLUMNS.map((c) => ({ id: c.id, title: getColumnDisplayName(c.id) }))

  if (!synced) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        {t('board.syncing')}
      </div>
    )
  }

  return (
    <div className="relative h-full">
      <DndContext
        sensors={sensors}
        collisionDetection={multiContainerCollision}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 p-6 h-full overflow-x-auto">
          {COLUMNS.map((column) => {
            const columnTasks = tasksForColumn(column.id)
            const taskIds = columnTasks.map((t) => t.id)

            return (
              <div
                key={column.id}
                className={cn('flex flex-col w-80 flex-shrink-0 rounded-lg', column.color)}
              >
                <div
                  className={cn(
                    'flex items-center justify-between p-3 rounded-t-lg',
                    column.headerColor
                  )}
                >
                  <div className="flex items-center gap-2">
                    {editingColumn === column.id ? (
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => saveColumnName(column.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveColumnName(column.id)
                          if (e.key === 'Escape') setEditingColumn(null)
                        }}
                        className="h-7 text-sm font-semibold w-32"
                        autoFocus
                      />
                    ) : (
                      <h3
                        className={cn(
                          'font-semibold text-foreground',
                          column.id !== 'done' && 'cursor-pointer'
                        )}
                        onDoubleClick={() => {
                          if (column.id === 'done') return
                          setEditingColumn(column.id)
                          setEditingName(getColumnDisplayName(column.id))
                        }}
                      >
                        {getColumnDisplayName(column.id)}
                      </h3>
                    )}
                    <Badge variant="secondary" className="rounded-full text-xs">
                      {columnTasks.length}
                    </Badge>
                  </div>
                  {column.id === 'todo' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setPanelMode('create')
                        setPanelTask(null)
                        setPanelExpanded(false)
                      }}
                      aria-label={t('board.addTask')}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                  <DroppableColumn columnId={column.id}>
                    {columnTasks.map((task) => (
                      <SortableTaskCard
                        key={task.id}
                        task={task}
                        isRemoteDragging={isRemoteDragging(task.id)}
                        onClick={() => handleOpenDetail(task)}
                        memberMap={memberMap}
                      />
                    ))}
                  </DroppableColumn>
                </SortableContext>
              </div>
            )
          })}
        </div>

        <DragOverlay>{activeTask ? <TaskCardOverlay task={activeTask} /> : null}</DragOverlay>
      </DndContext>

      {/* Task panel overlay */}
      <TaskPanel
        projectId={projectId}
        task={panelMode === 'edit' ? livePanelTask : null}
        open={panelMode !== 'closed'}
        onClose={handlePanelClose}
        onSubmit={handleCreateTask}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        onMoveTask={handleMoveTask}
        currentColumn={
          livePanelTask ? (findColumnForTask(livePanelTask.id) ?? undefined) : undefined
        }
        columns={columnDefs}
        expanded={panelExpanded}
        onExpandedChange={setPanelExpanded}
      />
    </div>
  )
}
